import { AnimatedSection } from "./AnimatedSection"
import { FloatingCard } from "./FloatingCard"
import { Upload, Settings, CheckCircle2, Calendar, BarChart3, Eye, Instagram, Facebook, Linkedin, Globe, Clock } from "lucide-react"

export function HowItWorksSection() {
  const steps = [
    {
      number: "1",
      title: "Input",
      subtitle: "2 Minuten",
      description: "Sie geben den Startschuss:",
      icon: Upload,
      items: [
        "Laden Sie ein Foto hoch",
        "Oder: Beantworten Sie 3 Fragen",
        "Oder: Lassen Sie das Team News-Kommentare erstellen"
      ]
    },
    {
      number: "2",
      title: "Processing",
      subtitle: "Automatisch",
      description: "Ihr Team arbeitet parallel:",
      icon: Settings,
      items: [
        "Visual Designer erstellt 4 Plattform-Varianten",
        "Copywriter schreibt 4 Captions (LinkedIn, Facebook, Instagram, Google)",
        "Brand Guardian prüft Markenkonsistenz",
        "Performance Analyst wählt optimale Posting-Zeiten",
        "Scheduler plant Publishing"
      ]
    },
    {
      number: "3",
      title: "Output",
      subtitle: "Optional: Mit Ihrer Freigabe",
      description: "Content geht live:",
      icon: CheckCircle2,
      items: [
        "4 Posts auf 4 Plattformen",
        "Zu optimalen Zeiten (datenbasiert)",
        "Performance wird getrackt",
        "Nächster Content-Cycle beginnt automatisch"
      ]
    }
  ]

  const timelineEvents = [
    {
      time: "Montag, 9:15 Uhr",
      title: "Sie starten",
      description: "Sie laden ein Foto hoch: Ihre neue Herbst-Kürbissuppe.",
      detail: 'Kontext: "Regionale Zutaten, neues Herbst-Menü"',
      icon: Upload,
      color: "text-primary"
    },
    {
      time: "Montag, 9:17 Uhr",
      title: "Ihr Team arbeitet bereits",
      description: "4 Agenten arbeiten parallel an Ihrem Content",
      icon: Settings,
      color: "text-primary",
      agents: [
        {
          role: "Visual Designer",
          icon: Eye,
          tasks: [
            "LinkedIn: Quadratisches Produktfoto mit Logo-Overlay",
            "Instagram: Vertical Story-Format mit Herbst-Filter",
            "Facebook: Landscape mit Zutaten-Closeup",
            "Google: Optimiert für Local Search Preview"
          ]
        },
        {
          role: "Copywriter",
          icon: Settings,
          captions: [
            {
              platform: "LinkedIn",
              icon: Linkedin,
              text: "Warum regionale Zutaten der Schlüssel zu authentischem Geschmack sind – unsere neue Kürbissuppe beweist es. 3 Gründe, warum wir auf Bauern aus der Region setzen →"
            },
            {
              platform: "Instagram",
              icon: Instagram,
              text: "Herbst auf dem Teller. Unsere Kürbissuppe mit Zutaten vom Bauern um die Ecke. Wer probiert mit? #regional #herbstküche #kürbissuppe"
            },
            {
              platform: "Facebook",
              icon: Facebook,
              text: "Wer liebt Kürbissuppe genauso sehr wie wir? Kommt vorbei und probiert unsere neue Herbst-Kreation! Rezept-Tipp im ersten Kommentar"
            },
            {
              platform: "Google My Business",
              icon: Globe,
              text: "Neue Herbstkarte! Kürbissuppe mit regionalen Zutaten. Jetzt probieren im [Ihr Restaurant] – [Adresse]"
            }
          ]
        },
        {
          role: "Brand Guardian",
          icon: CheckCircle2,
          checks: [
            "✓ Ton of Voice: Check (authentisch, regional, einladend)",
            "✓ Visuals: Check (Ihre Farbpalette, Logo-Platzierung)",
            "✓ Hashtags: Check (keine Off-Brand Tags)"
          ]
        },
        {
          role: "Performance Analyst",
          icon: Clock,
          schedule: [
            "LinkedIn: Di 8:00 Uhr (B2B Audience, morgens aktiv)",
            "Instagram: Mi 19:00 Uhr (Peak Engagement Zeit)",
            "Facebook: Mi 12:00 Uhr (Mittagspause, Food-Thema)",
            "Google: Do 11:00 Uhr (Local Search Peak)"
          ]
        }
      ]
    },
    {
      time: "Montag, 9:20 Uhr",
      title: "Vorschau & Freigabe",
      description: "Sie sehen Vorschau. Klicken \"Approve\" oder lassen durchlaufen.",
      icon: Eye,
      color: "text-primary"
    },
    {
      time: "Dienstag-Donnerstag",
      title: "Posts gehen live",
      description: "Posts gehen live zu optimalen Zeiten.",
      icon: Calendar,
      color: "text-primary"
    },
    {
      time: "Freitag",
      title: "Performance Report",
      description: "Ihr wöchentlicher Erfolgs-Report ist da",
      icon: BarChart3,
      color: "text-primary",
      metrics: [
        "2.847 Impressions",
        "127 Interactions (Likes, Comments, Shares)",
        "3 Reservierungen über Google",
        "12% höhere Engagement-Rate als Ihre letzten 10 Posts"
      ]
    }
  ]

  return (
    <section className="py-16 lg:py-24 bg-muted/30" data-testid="section-how-it-works">
      <div className="container px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-20">
          
          {/* Main Headline */}
          <AnimatedSection direction="up" className="text-center space-y-4">
            <div className="flex items-center justify-center gap-3">
              <Settings className="w-10 h-10 text-primary" />
              <h2 className="text-3xl lg:text-5xl font-bold font-playfair leading-tight">
                So funktioniert's
              </h2>
            </div>
            <p className="text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
              Von Ihrem Foto bis zum fertigen Post – in 3 einfachen Schritten
            </p>
          </AnimatedSection>

          {/* 3-Step Process Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="process-steps">
            {steps.map((step, index) => (
              <AnimatedSection 
                key={step.number} 
                delay={100 + index * 50} 
                direction="up"
                data-testid={`step-${step.number}`}
              >
                <FloatingCard className="h-full p-6 lg:p-8 space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <step.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-muted-foreground">SCHRITT {step.number}</div>
                      <h3 className="text-xl lg:text-2xl font-bold font-playfair">{step.title}</h3>
                      <p className="text-sm text-primary font-semibold">{step.subtitle}</p>
                    </div>
                  </div>
                  
                  <p className="font-semibold text-foreground">{step.description}</p>
                  
                  <ul className="space-y-2">
                    {step.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                        <span className="text-primary mt-1">→</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </FloatingCard>
              </AnimatedSection>
            ))}
          </div>

          {/* Practice Example Headline */}
          <AnimatedSection delay={250} direction="up" className="text-center space-y-4">
            <div className="flex items-center justify-center gap-3">
              <BarChart3 className="w-10 h-10 text-primary" />
              <h3 className="text-2xl lg:text-4xl font-bold font-playfair">
                Ein Beispiel aus der Praxis
              </h3>
            </div>
            <p className="text-lg lg:text-xl text-muted-foreground">
              Restaurant-Inhaber lädt ein Foto hoch – das passiert in den nächsten 5 Tagen
            </p>
          </AnimatedSection>

          {/* Timeline */}
          <div className="space-y-8" data-testid="timeline-example">
            {timelineEvents.map((event, index) => (
              <AnimatedSection 
                key={index} 
                delay={300 + index * 50} 
                direction="up"
              >
                <FloatingCard className="p-6 lg:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <event.icon className={`w-6 h-6 ${event.color}`} />
                    </div>
                    
                    <div className="flex-1 space-y-3">
                      <div>
                        <div className="text-sm font-semibold text-primary">{event.time}</div>
                        <h4 className="text-xl font-bold">{event.title}</h4>
                        <p className="text-muted-foreground">{event.description}</p>
                        {event.detail && (
                          <p className="text-sm italic text-muted-foreground mt-1">{event.detail}</p>
                        )}
                      </div>

                      {/* Agent Activities */}
                      {event.agents && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                          {event.agents.map((agent, i) => (
                            <div key={i} className="space-y-2">
                              <div className="flex items-center gap-2">
                                <agent.icon className="w-5 h-5 text-primary" />
                                <h5 className="font-bold text-foreground">{agent.role} →</h5>
                              </div>
                              
                              {agent.tasks && (
                                <div className="space-y-1 ml-7">
                                  <p className="text-sm font-semibold text-foreground">4 Varianten erstellt:</p>
                                  {agent.tasks.map((task, j) => (
                                    <p key={j} className="text-sm text-muted-foreground">- {task}</p>
                                  ))}
                                </div>
                              )}

                              {agent.captions && (
                                <div className="space-y-3 ml-7">
                                  <p className="text-sm font-semibold text-foreground">4 Captions geschrieben:</p>
                                  {agent.captions.map((caption, j) => (
                                    <div key={j} className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <caption.icon className="w-4 h-4 text-primary" />
                                        <p className="text-sm font-semibold text-foreground">{caption.platform}:</p>
                                      </div>
                                      <p className="text-sm text-muted-foreground italic">"{caption.text}"</p>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {agent.checks && (
                                <div className="space-y-1 ml-7">
                                  <p className="text-sm font-semibold text-foreground">Prüft Konsistenz:</p>
                                  {agent.checks.map((check, j) => (
                                    <p key={j} className="text-sm text-muted-foreground">{check}</p>
                                  ))}
                                </div>
                              )}

                              {agent.schedule && (
                                <div className="space-y-1 ml-7">
                                  <p className="text-sm font-semibold text-foreground">Wählt Posting-Zeiten:</p>
                                  {agent.schedule.map((time, j) => (
                                    <p key={j} className="text-sm text-muted-foreground">- {time}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Metrics */}
                      {event.metrics && (
                        <div className="space-y-2 mt-4">
                          <p className="font-semibold text-foreground">Performance Report:</p>
                          <ul className="space-y-1 ml-4">
                            {event.metrics.map((metric, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                                <span className="text-primary">-</span>
                                <span>{metric}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </FloatingCard>
              </AnimatedSection>
            ))}
          </div>

          {/* Closing Statement */}
          <AnimatedSection delay={500} direction="up">
            <div className="text-center space-y-4">
              <p className="text-xl lg:text-2xl font-bold font-playfair text-balance max-w-4xl mx-auto">
                Ihre Investition:{" "}
                <span className="text-primary">2 Minuten.</span>
              </p>
              <p className="text-xl lg:text-2xl font-bold font-playfair text-balance max-w-4xl mx-auto">
                Ihr Ergebnis:{" "}
                <span className="text-primary">4 professionelle Posts + messbare Ergebnisse.</span>
              </p>
            </div>
          </AnimatedSection>

        </div>
      </div>
    </section>
  )
}
